/**
 * Admin promo CRUD. Plan §Promocodes.
 *
 *   GET  /admin/promo                         list all codes + redemption counts
 *   POST /admin/promo                         create a new grant-credits code
 *   POST /admin/promo/:code/deactivate         kill switch
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BILLING_BASE, apiFetch } from './client.js';

export interface PromoCodeRow {
  code: string;
  kind: string;
  grantCredits: number | null;
  validFrom: string;
  validUntil: string | null;
  maxRedemptions: number | null;
  redemptionsUsed: number;
  segmentCountries: string[] | null;
  active: boolean;
  createdAt: string;
}

export const promoKeys = {
  all: ['admin-promo'] as const,
  list: () => [...promoKeys.all, 'list'] as const,
} as const;

export function usePromoCodesQuery() {
  return useQuery({
    queryKey: promoKeys.list(),
    queryFn: () =>
      apiFetch<{ codes: PromoCodeRow[] }>({
        method: 'GET',
        url: '/admin/promo',
        baseUrl: BILLING_BASE,
      }),
  });
}

export interface CreatePromoCodeArgs {
  code: string;
  grantCredits: number;
  validUntil?: string | null;
  maxRedemptions?: number | null;
  segmentCountries?: string[] | null;
}

export function useCreatePromoCodeMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: CreatePromoCodeArgs) =>
      apiFetch<{ ok: true; code: string } | { ok: false; reason: 'duplicate' | 'invalid' }>({
        method: 'POST',
        url: '/admin/promo',
        body: args,
        baseUrl: BILLING_BASE,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: promoKeys.list() });
    },
  });
}

export function useDeactivatePromoCodeMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (code: string) =>
      apiFetch<{ ok: boolean }>({
        method: 'POST',
        url: `/admin/promo/${encodeURIComponent(code)}/deactivate`,
        baseUrl: BILLING_BASE,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: promoKeys.list() });
    },
  });
}
